import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { memo } from "react";
import Tooltipable from "~/components/effects/tooltipable";

// Click tooltip max width — kept a named constant so a future design tweak
// lives in one place.
const TOOLTIP_MAX_WIDTH = 260;

// BK-3229 R4 AC 2.2.2 — info icon appearing next to a Compose-tab selector
// label when its current value was applied by a Library template click.
// Click opens a tooltip explaining which template chose the value and why.
// Disappears the moment the user manually changes the selector.
//
// Tooltipable uses react-bootstrap's OverlayTrigger with rootClose for the
// click trigger and cleans its own timers in an unmount-safe useEffect, so
// no extra cleanup is needed here.
const TemplateSetIndicator = ({
  isVisible,
  templateTitle = null,
  rationale = null,
  wasPlanFallback = false,
}) => {
  if (!isVisible) {
    return null;
  }

  const tooltipText = (
    <div className="text-left" style={{ maxWidth: TOOLTIP_MAX_WIDTH }}>
      <strong>Set by template</strong>
      <div className="small mt-1">{templateTitle}</div>
      {rationale && <div className="small mt-2">{rationale}</div>}
      {wasPlanFallback && (
        <div className="small mt-2 text-warning">
          Your plan doesn&apos;t include the recommended tier — using the
          nearest available instead. Upgrade for best results.
        </div>
      )}
    </div>
  );

  return (
    <Tooltipable trigger="click" placement="bottom" text={tooltipText}>
      <FontAwesomeIcon
        icon={["fal", "fa-info-circle"]}
        className="ml-1 text-primary text-xs cursor-pointer"
        data-testid="template-set-indicator"
      />
    </Tooltipable>
  );
};

TemplateSetIndicator.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  templateTitle: PropTypes.string,
  rationale: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  wasPlanFallback: PropTypes.bool,
};

// Selector toolbar re-renders on every textarea keystroke; memo shields the
// indicator (and its tooltip subtree) from every parent render.
export default memo(TemplateSetIndicator);
